/**
 * The multiplier pipeline. Every source of a multiplier (milestones, chains, Almanac upgrades,
 * goals, Heritage, festival perks, timed bonuses and the region) adds `IdleEffect`s to the list
 * in `idleEffects`. Every stat the world uses is read through `idleProducerStats`,
 * `idleBarnCapacityMs`, `idleHandCost` and `idleUpgradeCost`, so a new source needs no change
 * anywhere else.
 *
 * Factors of the same stat multiply.
 */
import type {
  IdleContent,
  IdleEffectSpecT,
  IdlePerkT,
  IdleProducerT,
  IdleTraitT,
} from '@hedgerow/data';
import {
  idleBarnBaseMs,
  idleClamp,
  idleHeritageMultiplier,
  idleIndex,
  idleChain,
  idleCycleMs,
  idleCyclePayout,
  idleHandFloor,
  idleHandPrice,
  idleMilestonesReached,
  idleProducer,
  idleUnitCost,
} from './rules.ts';
import { idleGoalParse, idleGoalReward, idleLongGoalsMet } from './goals.ts';
import { idleLinkBonuses } from './arrange.ts';
import { idleRosetteMultiplier } from './rosettes.ts';
import type { IdleWorldState, IdleStat } from './state.ts';

export interface IdleEffect {
  /** Where it comes from, for the screen: `milestone:wheat:10`, `chain:mill`, `bonus:market_day`. */
  source: string;
  stat: IdleStat;
  /** One producer, or every producer when absent. */
  producer?: string;
  factor: number;
}

/** An effect from content. A synergy (D251) grows with the count of the producer it names. */
function effectOf(source: string, spec: IdleEffectSpecT, state?: IdleWorldState): IdleEffect {
  let factor = spec.factor;
  if (spec.per) {
    const owned = state?.year.producers[spec.per.producer]?.owned ?? 0;
    factor = 1 + (spec.factor - 1) * Math.floor(owned / spec.per.every);
  }
  const e: IdleEffect = { source, stat: spec.stat, factor };
  if (spec.producer !== undefined) e.producer = spec.producer;
  return e;
}

/** D295: a perk's multiplier with `level` levels owned: its factor once per level, compounded. */
export function idlePerkFactor(perk: IdlePerkT, level: number): number {
  return perk.effect && level > 0 ? perk.effect.factor ** level : 1;
}

/** Every multiplier in force on the world at time `t`. */
export function idleEffects(state: IdleWorldState, content: IdleContent, t: number): IdleEffect[] {
  const out: IdleEffect[] = [];
  const ix = idleIndex(content);
  for (const [id, ps] of Object.entries(state.year.producers)) {
    const p = idleProducer(content, id);
    if (!p) continue;
    for (const m of idleMilestonesReached(p, ps.owned))
      out.push({
        source: `milestone:${id}:${m.owned}`,
        stat: m.kind,
        producer: id,
        factor: m.multiplier,
      });
  }
  for (const id of state.year.chains) {
    const ch = idleChain(content, id);
    if (ch)
      out.push({
        source: `chain:${id}`,
        stat: 'value',
        producer: ch.producer,
        factor: ch.multiplier,
      });
  }
  for (const id of state.year.upgrades) {
    const u = ix.upgrades.get(id);
    if (!u) continue;
    // D454: an effect on a group lifts each producer in it, and none in a theme without groups.
    const group = u.effect.group;
    if (group === undefined) out.push(effectOf(`upgrade:${id}`, u.effect, state));
    else
      for (const p of content.producers)
        if (p.group === group)
          out.push(effectOf(`upgrade:${id}`, { ...u.effect, producer: p.id }, state));
  }
  // D457: each mastery level gives its branch's lift once more, compounded.
  for (const [branch, level] of Object.entries(state.year.mastery ?? {})) {
    const lift = content.tree.mastery.effects[branch as keyof typeof content.tree.mastery.effects];
    if (lift && level && level > 0)
      out.push({ source: `mastery:${branch}`, stat: lift.stat, factor: lift.factor ** level });
  }
  // D334: each goal tier met pays its own reward; long goals' last the generation.
  for (const key of [...state.year.goals, ...idleLongGoalsMet(state)]) {
    const { id, tier } = idleGoalParse(key);
    const g = ix.goals.get(id);
    if (g) out.push(effectOf(`goal:${key}`, idleGoalReward(content, g, tier), state));
  }
  // Heritage earned raises all income for good (D244, D246), more slowly than it is earned (D249).
  const heritage = state.permanent.heritageEarned;
  if (heritage > 0)
    out.push({
      source: 'heritage',
      stat: 'value',
      factor: idleHeritageMultiplier(content, heritage),
    });
  for (const perk of content.perks) {
    const level = state.permanent.perks[perk.id] ?? 0;
    if (level > 0 && perk.effect)
      out.push(
        effectOf(`perk:${perk.id}`, { ...perk.effect, factor: idlePerkFactor(perk, level) }),
      );
  }
  for (const b of state.bonuses) {
    if (t < b.from || t >= b.until) continue;
    const e: IdleEffect = { source: `bonus:${b.source}`, stat: b.stat, factor: b.factor };
    if (b.producer !== undefined) e.producer = b.producer;
    out.push(e);
  }
  // The region goal's reward (D244), which the server sets on the state.
  const pb = state.region;
  if (pb && t >= pb.from && t < pb.until)
    out.push({ source: 'region:goal', stat: 'value', factor: pb.factor });
  // D454 step 3: a producer beside the plot that feeds it earns more, capped in the data.
  for (const [id, lb] of Object.entries(idleLinkBonuses(state, content)))
    out.push({ source: `link:${id}`, stat: 'value', producer: id, factor: 1 + lb.bonus });
  // Rosettes raise all income for good, compounding, capped in the data (D326).
  const rosetteFactor = idleRosetteMultiplier(state, content);
  if (rosetteFactor > 1) out.push({ source: 'rosettes', stat: 'value', factor: rosetteFactor });
  return out;
}

/** The product of every factor of `stat` that applies to `producer` (or to the world). */
export function idleFactor(effects: IdleEffect[], stat: IdleStat, producer?: string): number {
  let f = 1;
  for (const e of effects)
    if (e.stat === stat && (e.producer === undefined || e.producer === producer)) f *= e.factor;
  return f;
}

export interface IdleProducerStats {
  /** Time for one bar to fill. */
  cycleMs: number;
  /** Coins one full bar pays. */
  payout: number;
  /** Coins one tapped bar pays. */
  tapPayout: number;
  /** Coins a second while the bar runs without a break. */
  rate: number;
}

/** A producer's bar length and payouts, with every effect applied. `owned` overrides the count. */
export function idleProducerStats(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  effects: IdleEffect[],
  owned = state.year.producers[id]?.owned ?? 0,
): IdleProducerStats {
  const p = idleProducer(content, id);
  if (!p || owned <= 0) return { cycleMs: p?.cycleMs ?? 1, payout: 0, tapPayout: 0, rate: 0 };
  // Milestones depend on the count, so a hypothetical count swaps in its own milestones.
  // One pass, in the order of `effects`, so the products match idleFactor exactly.
  const swap = owned !== (state.year.producers[id]?.owned ?? 0);
  const prefix = `milestone:${id}:`;
  let speed = 1;
  let value = 1;
  let tap = 1;
  const add = (stat: IdleStat, factor: number) => {
    if (stat === 'speed') speed *= factor;
    else if (stat === 'value') value *= factor;
    else if (stat === 'tap') tap *= factor;
  };
  for (const e of effects) {
    if (e.producer !== undefined && e.producer !== id) continue;
    if (swap && e.producer === id && e.source.startsWith(prefix)) continue;
    add(e.stat, e.factor);
  }
  if (swap) for (const m of idleMilestonesReached(p, owned)) add(m.kind, m.multiplier);
  const cycleMs = idleCycleMs(p, speed);
  const payout = idleCyclePayout(p, owned, value);
  const tapPayout = Math.floor(payout * tap);
  return { cycleMs, payout, tapPayout, rate: idleClamp((payout * 1000) / cycleMs) };
}

/**
 * Every owned producer's stats: what `idleProducerStats` gives for each, factors multiplied in
 * the same order so the figures are identical, without matching every effect's source once per
 * producer. The advance and income use it.
 */
export function idleAllProducerStats(
  state: IdleWorldState,
  content: IdleContent,
  effects: IdleEffect[],
): Record<string, IdleProducerStats> {
  const acc: Record<string, { speed: number; value: number; tap: number }> = {};
  const ids = Object.keys(state.year.producers);
  for (const id of ids) acc[id] = { speed: 1, value: 1, tap: 1 };
  const add = (f: { speed: number; value: number; tap: number }, e: IdleEffect) => {
    if (e.stat === 'speed') f.speed *= e.factor;
    else if (e.stat === 'value') f.value *= e.factor;
    else if (e.stat === 'tap') f.tap *= e.factor;
  };
  for (const e of effects) {
    if (e.stat !== 'speed' && e.stat !== 'value' && e.stat !== 'tap') continue;
    if (e.producer === undefined) for (const id of ids) add(acc[id]!, e);
    else if (acc[e.producer]) add(acc[e.producer]!, e);
  }
  const out: Record<string, IdleProducerStats> = {};
  for (const id of ids) {
    const p = idleProducer(content, id);
    const owned = state.year.producers[id]?.owned ?? 0;
    if (!p || owned <= 0) {
      out[id] = { cycleMs: p?.cycleMs ?? 1, payout: 0, tapPayout: 0, rate: 0 };
      continue;
    }
    const f = acc[id]!;
    const cycleMs = idleCycleMs(p, f.speed);
    const payout = idleCyclePayout(p, owned, f.value);
    const tapPayout = Math.floor(payout * f.tap);
    out[id] = { cycleMs, payout, tapPayout, rate: idleClamp((payout * 1000) / cycleMs) };
  }
  return out;
}

/**
 * The barn's capacity in milliseconds. It comes from the barn's level only (D253); `effects` is
 * kept in the signature for callers and no longer changes it.
 */
export function idleBarnCapacityMs(
  state: IdleWorldState,
  content: IdleContent,
  _effects: IdleEffect[],
): number {
  return Math.round(idleBarnBaseMs(content, state.permanent.barnLevel));
}

/**
 * A producer as this world buys it: its cost growth lowered by the world's Lineage traits (D264),
 * and its base price scaled by the festivals held this generation, `prices.unitPerFestival` each
 * (D367), as an unlock is (D352). Festivals change only between years, so the price is set when
 * the year starts; income and bonuses never move it. Pass the result to `idleUnitCost`,
 * `idleBuyCost` and `idleBuyMax` for the costs the world pays.
 */
export function idleProducerFor(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
): IdleProducerT | undefined {
  const p = idleProducer(content, id);
  if (!p) return undefined;
  const traits = state.permanent.lineage?.traits ?? {};
  let cut = 0;
  for (const t of content.lineage.traits) cut += idleTraitGrowthCut(t, traits[t.id] ?? 0);
  const held = state.permanent.festivals.length - (state.permanent.lineage?.festivalsBefore ?? 0);
  const scale = Math.pow(content.prices.unitPerFestival, Math.max(0, held));
  if (cut === 0 && scale === 1) return p;
  return {
    ...p,
    baseCost: idleClamp(p.baseCost * scale),
    costGrowth: cut === 0 ? p.costGrowth : Math.max(1.05, p.costGrowth - cut),
  };
}

/** D295: how much lower a trait makes every producer's cost growth with `level` levels owned. */
export function idleTraitGrowthCut(trait: IdleTraitT, level: number): number {
  return (trait.costGrowthCut ?? 0) * level;
}

/**
 * Coins to hire the hand for a producer (D352): `idleHandPrice` (its own price in the first
 * year, then its next `prices.handUnits` units if more), with every hand-cost effect applied,
 * but never below `idleHandFloor`. It moves only when that crop is bought: never with income or
 * a bonus. Effects cut the price only down to the floor (D307's bug). The units are priced as
 * the world buys them, with its Lineage traits' lower cost growth.
 */
export function idleHandCost(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  effects: IdleEffect[],
): number {
  const p = idleProducerFor(state, content, id);
  if (!p) return 0;
  const owned = state.year.producers[id]?.owned ?? 1;
  const held = state.permanent.festivals.length - (state.permanent.lineage?.festivalsBefore ?? 0);
  // D386: times the year's price factor, set when the year starts.
  const own =
    idleHandPrice(content, p, owned, held) *
    (state.year.priceFactor ?? 1) *
    idleFactor(effects, 'hand', id);
  const floor = idleHandFloor(content, idleUnitCost(p, owned));
  return idleClamp(Math.round(Math.max(own, floor)));
}

/** Coins for an Almanac upgrade, with every upgrade-cost effect applied. */
export function idleUpgradeCost(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  effects: IdleEffect[],
): number {
  const u = idleIndex(content).upgrades.get(id);
  if (!u) return 0;
  // D334: a page that grows with the world costs more for each festival held past its opening.
  const held = state.permanent.festivals.length - (state.permanent.lineage?.festivalsBefore ?? 0);
  const past = Math.max(0, held - (u.needsFestivals ?? 0));
  const grown = u.costPerFestival ? u.cost * u.costPerFestival ** past : u.cost;
  return idleClamp(Math.round(grown * idleFactor(effects, 'upgrade')));
}
